'use client';

import React from 'react';
import Link from 'next/link';
import { ExternalLink, AlertCircle } from 'lucide-react';
import { VENDORS } from '@/lib/mockData';
import StatusBadge from '@/components/ui/StatusBadge';

// Backend integration point: replace with /api/contracts/expiring?days=90
function getDaysUntil(dateStr: string): number {
  const now = new Date('2026-03-29');
  const target = new Date(dateStr);
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export default function ExpiringContractsTable() {
  const expiring = VENDORS
    .filter((v) => v.contractEnd && getDaysUntil(v.contractEnd) <= 90 && getDaysUntil(v.contractEnd) > 0)
    .sort((a, b) => new Date(a.contractEnd).getTime() - new Date(b.contractEnd).getTime());

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--border))]">
        <div>
          <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Contracts Expiring Within 90 Days</h2>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">{expiring.length} contracts require renewal decision</p>
        </div>
        <Link href="/vendor-management" className="btn-ghost text-xs">View All</Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]">
              <th className="text-left px-4 py-2 font-medium text-[hsl(var(--text-muted))] whitespace-nowrap">Vendor</th>
              <th className="text-left px-4 py-2 font-medium text-[hsl(var(--text-muted))] whitespace-nowrap">Department</th>
              <th className="text-left px-4 py-2 font-medium text-[hsl(var(--text-muted))] whitespace-nowrap">Risk</th>
              <th className="text-left px-4 py-2 font-medium text-[hsl(var(--text-muted))] whitespace-nowrap">Expiry</th>
              <th className="text-left px-4 py-2 font-medium text-[hsl(var(--text-muted))] whitespace-nowrap">Days Left</th>
              <th className="text-left px-4 py-2 font-medium text-[hsl(var(--text-muted))] whitespace-nowrap">Status</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--border))]">
            {expiring.map((v) => {
              const days = getDaysUntil(v.contractEnd);
              const urgencyColor = days <= 30 ? 'text-red-600 dark:text-red-400 font-semibold' : days <= 60 ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-[hsl(var(--text-secondary))]';
              return (
                <tr key={v.id} className="table-row-hover transition-colors">
                  <td className="px-4 py-2.5">
                    <div>
                      <p className="font-medium text-[hsl(var(--text-primary))]">{v.name}</p>
                      <p className="text-2xs text-[hsl(var(--text-muted))]">{v.vendorId}</p>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-[hsl(var(--text-secondary))] whitespace-nowrap">{v.department}</td>
                  <td className="px-4 py-2.5">
                    <StatusBadge type="risk" value={v.riskTier} />
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[hsl(var(--text-secondary))] tabular-nums whitespace-nowrap">
                    {v.contractEnd}
                  </td>
                  <td className={`px-4 py-2.5 tabular-nums whitespace-nowrap ${urgencyColor}`}>
                    {days <= 30 && <AlertCircle size={10} className="inline mr-1" />}
                    {days}d
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge type="contract" value={v.contractStatus} />
                  </td>
                  <td className="px-4 py-2.5">
                    <Link href="/vendor-management" className="btn-ghost p-1">
                      <ExternalLink size={12} />
                    </Link>
                  </td>
                </tr>
              );
            })}
            {expiring.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-xs text-[hsl(var(--text-muted))]">
                  No contracts expiring within 90 days.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}